import ButtonIcon from "components/button-icon";
import { CheckCircle } from 'components/icons/check-circle.icon';
import { useOpenSuggestionPanel } from "components/suggestion-context/context/openSuggestionPanelContext";
import { useGlobalContext } from "hooks/useGlobalContext";
import { useSuggestionContext } from 'pages/suggestions/context/suggestionsContext';
import { useState } from 'react';
import intl from "react-intl-universal";
import { searchJustificationsRequestInOver } from "services/suggestion/suggestion.service";
import Session from 'utils/user-storage';
import {
  getOtherSuggestions,
  getStockReturnSuggestions,
} from "../../utils/suggestion-type";
import StockReturnNotificationModal from "../stock-return-notification-modal";
import { ModalSuggestionsInOver } from './modal-suggestions-in-over';
import ModalSuggestionsMassAcceptance from "./modal-suggestions-mass-acceptance";
import {
  disableButtonAcceptSuggestions,
  getExternalSuggestionRequest,
  getSuggestionAcceptanceRequests,
  getSuggestionsForAcceptance,
  getSuggestionsForAcceptanceInOver,
} from "./utils";

export const SuggestionsMassAcceptance = () => {
  const { suggestionsSelecteds, suggestions, fetchSuggestions, setSuggestionsSelecteds } =
    useSuggestionContext();
  const { startOperation, operation, showFeedback } = useGlobalContext();
  const { closeSuggestionPanel } = useOpenSuggestionPanel();
  const selectedOtherSuggestions = getOtherSuggestions(suggestionsSelecteds);
  const selectedStockReturnSuggestions = getStockReturnSuggestions(suggestionsSelecteds);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [openJustificationModal, setOpenJustificationModal] = useState(false);
  const [openStockReturnModal, setOpenStockReturnModal] = useState(false);
  const [stockReturnSuggestions, setStockReturnSuggestions] = useState([]);
  const [justificationForOver, setJustificationForOver] = useState(null);
  const [justificationsList, setJustificationsList] = useState([]);

  const handleCloseConfirmationModal = () => setOpenConfirmationModal(false);

  const handleAcceptClick = () => {
    closeSuggestionPanel();
    if (selectedStockReturnSuggestions.length > 0 && selectedOtherSuggestions.length === 0) {
      setStockReturnSuggestions(selectedStockReturnSuggestions);
      setOpenStockReturnModal(true);
      return;
    }

    setOpenConfirmationModal(true);
  };

  const handleStockReturnNotificationSuccess = async () => {
    setSuggestionsSelecteds?.([]);
    setStockReturnSuggestions([]);

    if (suggestions?.filters && fetchSuggestions) {
      try {
        await fetchSuggestions(suggestions.filters);
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleSuggestionsMassAcceptance = async () => {
    if (getSuggestionsForAcceptanceInOver(selectedOtherSuggestions).length > 0) {
      const _justifications = await searchJustificationsRequestInOver();
      setJustificationsList(_justifications);
      handleCloseConfirmationModal();
      setOpenJustificationModal(true);
    } else {
      acceptSuggestionsInMass();
    }
  }

  const refreshAfterAcceptance = async () => {
    setSuggestionsSelecteds?.([]);
    if (suggestions?.filters && fetchSuggestions) {
      try {
        await fetchSuggestions(suggestions.filters);
      } catch (error) {
        console.error(error);
      }
    }
  };

  const acceptSuggestionsInMass = (stockReturnPayload = null) => {
    try {
      const _suggestions = getSuggestionsForAcceptance(selectedOtherSuggestions);
      const _userId = Session.get().Id;
      const stockReturnItems = (stockReturnPayload?.items || []).map((item) => ({
        ...item,
        labeledFamilyCenterId: selectedStockReturnSuggestions.find(
          (suggestion) => String(suggestion.Id) === String(item.suggestionId)
        )?.LabeledFamilYCenterId,
      }));
      const _requests = [
        ...getSuggestionAcceptanceRequests(
          _suggestions,
          _userId,
          justificationForOver,
          stockReturnPayload ? { ...stockReturnPayload, items: stockReturnItems } : null
        ),
        ..._suggestions
          .filter((item) => item.ExternalSuggestion)
          .map((item) => getExternalSuggestionRequest(item, _userId, justificationForOver)),
      ];
      const _text = intl.get('suggestions.operationText');
      if (selectedStockReturnSuggestions.length > 0 && !stockReturnPayload) {
        setStockReturnSuggestions(selectedStockReturnSuggestions);
        setOpenStockReturnModal(true);
      } else if (_requests.length > 0 && stockReturnPayload) {
        return new Promise((resolve) => startOperation(_requests, _text, resolve));
      } else if (_requests.length > 0) {
        startOperation(_requests, _text, refreshAfterAcceptance);
      }
    } catch (error) {
      console.error(error);
    } finally {
      handleCloseConfirmationModal();
      setOpenJustificationModal(false);
    }
  }

  const suggestionsForMassAcceptance = getSuggestionsForAcceptance(selectedOtherSuggestions);
  const suggestionsInOverForMassAcceptance = getSuggestionsForAcceptanceInOver(
    selectedOtherSuggestions
  );

  return (
    <>
      <ButtonIcon
        buttonIcon={<CheckCircle />}
        buttonText={intl.get("suggestions.acceptSuggestions")}
        buttonType="default"
        disabled={disableButtonAcceptSuggestions(suggestionsSelecteds, operation.inProgress)}
        onClick={handleAcceptClick}
        style={{ padding: "0px 12px", marginRight: "8px" }}
      />

      <ModalSuggestionsMassAcceptance
        open={openConfirmationModal}
        close={handleCloseConfirmationModal}
        onClick={handleSuggestionsMassAcceptance}
        suggestions={suggestionsForMassAcceptance}
        stockReturnSuggestions={selectedStockReturnSuggestions}
        suggestionsInOver={suggestionsInOverForMassAcceptance}
      />

      <ModalSuggestionsInOver
        open={openJustificationModal}
        close={() => setOpenJustificationModal(false)}
        justificationForOver={justificationForOver}
        setJustificationForOver={setJustificationForOver}
        justificationsList={justificationsList}
        acceptSuggestionsInMass={acceptSuggestionsInMass}
        showFeedback={showFeedback}
      />

      <StockReturnNotificationModal
        open={openStockReturnModal}
        suggestions={stockReturnSuggestions}
        onClose={() => setOpenStockReturnModal(false)}
        onSuccess={handleStockReturnNotificationSuccess}
        onSubmit={(stockReturnPayload) => acceptSuggestionsInMass(stockReturnPayload)}
      />
    </>
  );
}
